import { useCallback, useId, useMemo } from 'react';
import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';

import { useTheme } from '@/contexts/ThemeContext';
import { logger } from '@/lib/logger';
import type { ChartDatapointSelection, ChartType } from '@/types/reports';

import type { ChartConfiguration, LineChartOptions } from '../types';
import { computeValueAxisMeta } from '../utils/chartAxisMeta';
import type { MeasureUnitGroup } from '../utils/measureUnits';
import {
  isDimmed,
  makeCellKeyDownHandler,
  makeSeriesClickHandler,
  markOpacity,
  sourceRowIndexOf,
  useSeriesKeyboardSelection,
} from './chartSelection';
import { FacetedCartesianChart } from './FacetedCartesianChart';
import { CartesianLegend, ChartCartesianGrid, ChartTooltip } from './parts';
import {
  AXIS_TICK_FONT_SIZE,
  CARTESIAN_CHART_MARGIN,
  categoryAxisHeight,
  CHART_SERIES_ANIMATION,
  curveTypeForLineStyle,
  FACET_YAXIS_WIDTH,
  formatAxisValue,
  mapScale,
  resolveCssColor,
  valueAxisProps,
  xAxisLabelProps,
} from './shared';

interface ComboChartCaseProps {
  chartType: ChartType;
  unitGroups: MeasureUnitGroup[];
  chartData: Record<string, unknown>[];
  config: ChartConfiguration;
  columnTitles: Record<string, string>;
  columnTypes: Record<string, string>;
  currency: string;
  seriesColors: Record<string, string>;
  height: number;
  legendAlign: 'left' | 'center' | 'right';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  tooltipFormatter: (value: any, name: any, entry: any) => any;
  categoryBottomLabel?: string;
  /** The resolved left-axis title from ChartRendererCore, used only when
   * the bar group has more than one series (a single series names its own
   * axis via its column title). */
  valueLeftLabel?: string;
  lineOptions: LineChartOptions;
  /**
   * `'stack'` only when `chartType === 'bar'` and the saved bar options
   * request stacking; `undefined` otherwise. The caller resolves this —
   * ComboChartCase never reads `config.chartOptions` itself, since on a
   * line/area report it is not `BarChartOptions`.
   */
  barStackId?: string;
  /** Called when the user clicks (or keyboard-activates) a chart datapoint. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint, used to dim all others. */
  activeSelection?: ChartDatapointSelection | null;
}

/** Hoisted: avoids creating a new object on every render when interactive. */
const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

/**
 * Renders incommensurate measures as a conventional combination chart: the
 * currency-unit group as bars on the left axis, the number-unit group as a
 * line on a right axis — both against one shared x-axis and one shared
 * legend. Bars always carry the currency group and the line always carries
 * the number group, regardless of the report's saved `chart_type` (a saved
 * line or area report still renders its currency measures as bars here);
 * `resolveMultiMeasureLayout` is what decides a chart lands on this combo
 * layout versus stacked panels versus a single shared axis.
 */
export function ComboChartCase({
  chartType,
  unitGroups,
  chartData,
  config,
  columnTitles,
  columnTypes,
  currency,
  seriesColors,
  height,
  legendAlign,
  tooltipFormatter,
  categoryBottomLabel,
  valueLeftLabel,
  lineOptions,
  barStackId,
  onDatapointSelect,
  activeSelection,
}: ComboChartCaseProps) {
  // Only used by the defensive fallback below — computed unconditionally so
  // the hook always runs in the same order (Rules of Hooks).
  const fallbackGradientPrefix = useId();

  // Stable click handler factory — wraps makeSeriesClickHandler so the returned
  // per-key handler is only recreated when onDatapointSelect changes.
  const makeClickHandler = useCallback(
    (seriesKey: string) => makeSeriesClickHandler(seriesKey, onDatapointSelect),
    [onDatapointSelect]
  );

  const barGroup = unitGroups.find(group => group.unit === 'currency');
  const lineGroup = unitGroups.find(group => group.unit === 'number');

  // Keyboard-selection hook for the Line series: tracks the last hovered point
  // and defaults to the first point of the first line series on Enter/Space.
  // Must be called before the early return (Rules of Hooks).
  const { handleMouseMove, handleContainerKeyDown } =
    useSeriesKeyboardSelection({
      chartData,
      seriesKeys: lineGroup?.keys ?? [],
      onDatapointSelect,
    });

  // Subscribe to the theme so a toggle re-renders this chart; the tick `fill`
  // is a resolved hex (an SVG attribute, not a live var()), so without a
  // re-render it would keep the old theme's color. Resolving the color in
  // render keys the memo on the value itself: the object reference stays stable
  // when the theme is unchanged and updates when the DOM's data-theme flips.
  useTheme();
  const tickColor = resolveCssColor('--color-text-secondary');
  const xAxisTick = useMemo(
    () => ({ fill: tickColor, fontSize: AXIS_TICK_FONT_SIZE }),
    [tickColor]
  );

  const interactive = Boolean(onDatapointSelect);

  if (!barGroup || !lineGroup) {
    // resolveMultiMeasureLayout only returns 'combo' for exactly two unit
    // groups, and MeasureUnit only has 'currency' | 'number' — so this
    // should be unreachable. Guard defensively rather than crash if that
    // classification ever changes.
    logger.error(
      'ComboChartCase resolved without both a currency and a number unit group',
      undefined,
      {
        component: 'ChartRenderer',
        units: unitGroups.map(group => group.unit),
      }
    );
    let effectiveRenderer: 'bar' | 'line' | 'area';
    if (chartType === 'bar') {
      effectiveRenderer = 'bar';
    } else if (chartType === 'area' || lineOptions.areaFill !== 'none') {
      effectiveRenderer = 'area';
    } else {
      effectiveRenderer = 'line';
    }
    return (
      <FacetedCartesianChart
        chartType={chartType}
        effectiveRenderer={effectiveRenderer}
        unitGroups={unitGroups}
        chartData={chartData}
        config={config}
        columnTitles={columnTitles}
        columnTypes={columnTypes}
        currency={currency}
        seriesColors={seriesColors}
        height={height}
        legendAlign={legendAlign}
        tooltipFormatter={tooltipFormatter}
        categoryBottomLabel={categoryBottomLabel}
        gradientPrefix={fallbackGradientPrefix}
        lineOptions={lineOptions}
        onDatapointSelect={onDatapointSelect}
        activeSelection={activeSelection}
      />
    );
  }

  const barAxisMeta = computeValueAxisMeta(
    chartData,
    barGroup.keys,
    columnTypes,
    config,
    currency
  );
  const lineAxisMeta = computeValueAxisMeta(
    chartData,
    lineGroup.keys,
    columnTypes,
    config,
    currency
  );
  // Known limitation: computeValueAxisMeta reads config.axes?.yAxisLeft.scale
  // for both axes — nothing sets a log scale independently per axis today.

  const barAxisTitle =
    barGroup.keys.length === 1
      ? columnTitles[barGroup.keys[0]]
      : valueLeftLabel;
  // The right axis must never fall back to the left label.
  const lineAxisTitle =
    lineGroup.keys.length === 1
      ? columnTitles[lineGroup.keys[0]]
      : config.axes?.yAxisRight?.label;

  const xAxisHeight = categoryAxisHeight({
    hasTitle: Boolean(categoryBottomLabel),
  });

  const barTitles = barGroup.keys
    .map(key => columnTitles[key] || key)
    .join(', ');
  const lineTitles = lineGroup.keys
    .map(key => columnTitles[key] || key)
    .join(', ');
  const ariaLabel = `Combination chart: ${barTitles} as bars on the left axis, ${lineTitles} as a line on the right axis`;

  const legendPayload: LegendPayload[] = [
    ...barGroup.keys.map(key => ({
      value: columnTitles[key] || key,
      color: seriesColors[key],
      type: 'rect' as const,
    })),
    ...lineGroup.keys.map(key => ({
      value: columnTitles[key] || key,
      color: seriesColors[key],
      type: 'line' as const,
    })),
  ];

  return (
    <div
      role='group'
      aria-label={ariaLabel}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleContainerKeyDown : undefined}
      className={interactive ? 'outline-none' : undefined}
    >
      <ResponsiveContainer width='100%' height={height}>
        <ComposedChart
          data={chartData}
          barCategoryGap='20%'
          margin={CARTESIAN_CHART_MARGIN}
          onMouseMove={interactive ? handleMouseMove : undefined}
        >
          <ChartCartesianGrid
            show={config.axes?.xAxis.showGrid}
            yAxisId='left'
          />
          <XAxis
            dataKey='x'
            type='category'
            hide={!config.axes?.xAxis.show}
            axisLine={false}
            tickLine={false}
            tick={xAxisTick}
            height={xAxisHeight}
            label={xAxisLabelProps(categoryBottomLabel)}
            angle={config.axes?.xAxis.rotation}
          />
          <YAxis
            yAxisId='left'
            {...valueAxisProps({
              axisMeta: barAxisMeta,
              axisConfig: config.axes?.yAxisLeft,
              label: barAxisTitle,
              domainMin: barAxisMeta.defaultValueAxisMin,
              scale: mapScale(config.axes?.yAxisLeft.scale || 'linear'),
              tickFormatter: config.axes?.yAxisLeft?.unit
                ? (value: number) =>
                    formatAxisValue(value, config.axes?.yAxisLeft)
                : barAxisMeta.autoTickFormatter,
              ticks: barAxisMeta.smallIntegerTicks,
            })}
          />
          <YAxis
            yAxisId='right'
            orientation='right'
            {...valueAxisProps({
              axisMeta: lineAxisMeta,
              axisConfig: config.axes?.yAxisRight,
              label: lineAxisTitle,
              labelSide: 'right',
              width: FACET_YAXIS_WIDTH,
              domainMin: lineAxisMeta.defaultValueAxisMin,
              scale: mapScale('linear'),
              hide: config.axes?.yAxisRight
                ? !config.axes.yAxisRight.show
                : false,
              tickFormatter: config.axes?.yAxisRight?.unit
                ? (value: number) =>
                    formatAxisValue(value, config.axes?.yAxisRight)
                : lineAxisMeta.autoTickFormatter,
              ticks: lineAxisMeta.smallIntegerTicks,
            })}
          />
          <ChartTooltip
            show={config.tooltip.show}
            formatter={tooltipFormatter}
          />
          <CartesianLegend
            show={config.legend.show}
            legend={config.legend}
            legendAlign={legendAlign}
            payload={legendPayload}
          />
          {barGroup.keys.map(key => (
            <Bar
              key={key}
              yAxisId='left'
              dataKey={key}
              name={columnTitles[key] || key}
              fill={seriesColors[key]}
              stackId={barStackId}
              radius={[6, 6, 0, 0]}
              maxBarSize={48}
              onClick={makeClickHandler(key)}
              style={interactive ? POINTER_CURSOR_STYLE : undefined}
              {...CHART_SERIES_ANIMATION}
            >
              {interactive
                ? chartData.map((datum, cellIndex) => {
                    const rowIndex = sourceRowIndexOf(datum);
                    let dimmed = false;
                    if (
                      activeSelection !== null &&
                      activeSelection !== undefined
                    ) {
                      dimmed =
                        rowIndex !== null
                          ? isDimmed(activeSelection, rowIndex, key)
                          : true;
                    }
                    const xLabel =
                      typeof datum['x'] === 'string'
                        ? datum['x']
                        : String(cellIndex);
                    const cellKey =
                      rowIndex !== null ? `r${rowIndex}` : `o${cellIndex}`;
                    return (
                      <Cell
                        key={cellKey}
                        fillOpacity={markOpacity(dimmed)}
                        role={rowIndex !== null ? 'button' : undefined}
                        tabIndex={rowIndex !== null ? 0 : undefined}
                        aria-label={
                          rowIndex !== null
                            ? `${xLabel} — ${columnTitles[key] || key}`
                            : undefined
                        }
                        onKeyDown={makeCellKeyDownHandler(
                          datum,
                          key,
                          onDatapointSelect
                        )}
                      />
                    );
                  })
                : null}
            </Bar>
          ))}
          {lineGroup.keys.map(key => {
            // Per-point dimming on a Line is impractical; dim at series level.
            const seriesDimmed =
              activeSelection !== null &&
              activeSelection !== undefined &&
              isDimmed(activeSelection, activeSelection.rowIndex, key);
            return (
              <Line
                key={key}
                yAxisId='right'
                dataKey={key}
                name={columnTitles[key] || key}
                stroke={seriesColors[key]}
                strokeOpacity={markOpacity(seriesDimmed)}
                type={curveTypeForLineStyle(lineOptions.lineStyle)}
                strokeWidth={2}
                dot={
                  lineOptions.showPoints
                    ? {
                        fill: 'var(--color-bg-primary)',
                        stroke: seriesColors[key],
                        strokeWidth: 2,
                        r: 4,
                      }
                    : false
                }
                activeDot={{
                  fill: 'var(--color-bg-primary)',
                  stroke: seriesColors[key],
                  strokeWidth: 2,
                  r: 5,
                }}
                onClick={makeClickHandler(key)}
                {...CHART_SERIES_ANIMATION}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
